import {
  SynchrounousState,
  type VoidAsyncResult,
  asVoidAsyncResult,
  createResolvedAsyncResult,
} from "@/cache/AsyncResult";
import type { CustomGeoJSONAnimationFrameContext } from "@/cache/CustomGeoJSONCache/types";
import { constructHashId } from "@/cache/CustomGeoJSONCache/utils";
import { networkCaches } from "@/cache/GlobalCache";
import { customGeoJsonLayerConfig } from "@/constants/layerConfigAttributes";
import { isGeojsonBackendHydrationEnabled } from "@/feature-flags/non-hook/non-hook";
import { emptyGeoJsonSource } from "@/geojson";
import { ChangeDetector } from "@/layers/ChangeDetector";
import type { ScenePublicApi } from "@/layers/Compositor";
import { type AnimationPrefetchContext, type CheckedProps, LayerBase, type PropsChecker } from "@/layers/LayerBase";
import { customGeoJSONMapboxLayerGroup } from "@/layers/geojson/custom/utils";
import { clearLayerLoading, reportLayerLoading } from "@/layers/geojson/layerLoadingStore";
import { createGeojsonPOIPopover } from "@/layers/geojson/popovers/GeoJSONPopover";
import { advancedThresholdColorExpression } from "@/layers/utility/advancedThresholdColorExpression";
import type { LayerKindName } from "@/layers/utility/createLayerObject";
import {
  type MapboxLayerGroup,
  MapboxSourceGroup,
  addLayerGroup,
  addSource,
  moveZIndex,
  removeLayerGroup,
  removeSource,
  updateMapboxLayoutProperties,
  updateMapboxPaintProperties,
  updateSourceData,
} from "@/layers/utility/mapbox-utils";
import {
  thresholdColorExpression,
  thresholdColorExpressionFromProperties,
} from "@/layers/utility/thresholdColorExpression";
import { getCurrentAnimationPrefetchContext } from "@/models/time-control/animationPrefetchContext";
import {
  type AdvancedThreshold,
  type ThresholdColor,
  transformAdvancedThresholds,
  transformColorThresholds,
} from "@/overlay/components/CommonParts/Threshold/types";
import { roundToNearestCacheableTime } from "@/time/roundByLayerKind";
import { safeFmt } from "@/utility/safeFmt";
import type { BaseRequest } from "@mm/api.meteomatics.com";
import Logger from "@mm/logging";
import type {
  CustomGeoJSONCustomOptions,
  CustomGeoJSONLayerOutput,
  GuiTimeZone,
} from "@mm/metx-workbench.meteomatics.com";
import { isEmpty } from "lodash";
import type { DateTime } from "luxon";
import mapboxgl, { type Expression } from "mapbox-gl";
import "../style/geojson.scss";

const logger = Logger.fromFilename(__filename);

export type CustomGeoJsonRequestOption = Pick<CustomGeoJSONLayerOutput, "model" | "parameter_unit"> & {
  customOption: CustomGeoJSONCustomOptions;
  thresholds: ThresholdColor[];
  advancedThresholds: AdvancedThreshold[];
  user_file_id: number;
};

export class CustomGeoJSONLayerImpl extends LayerBase<CustomGeoJSONLayerOutput> {
  private dirty = true;
  private displayDateTime: ChangeDetector<string>;
  private isPending: ChangeDetector<boolean> = new ChangeDetector<boolean>(false);
  private thresholdFlag: ChangeDetector<boolean> = new ChangeDetector<boolean>(false);

  private mapboxLayerGroup: MapboxLayerGroup<
    | "line-layer"
    | "point-layer"
    | "polygon-layer"
    | "polygon-layer-outline"
    | "line-value-layer"
    | "polygon-value-layer"
  >;

  private mapboxSrcGroup: MapboxSourceGroup<"line-data-source" | "point-data-source" | "polygon-data-source">;
  private popup = new mapboxgl.Popup({ maxWidth: "90vw", className: "mapbox-popover mapbox-popover-geojson" });
  private animationPrefetchContext: CustomGeoJSONAnimationFrameContext | null = null;

  readonly mapbox_id: string;
  readonly mapbox_source: string;

  constructor(id: number, props: CustomGeoJSONLayerOutput, scene: ScenePublicApi, timezone: GuiTimeZone) {
    super(id, props, scene, timezone);
    this.displayDateTime = new ChangeDetector(
      roundToNearestCacheableTime(this.scene.getDisplayTimeWithOffset(), this.props.kind as LayerKindName).toISO(),
    );
    this.mapbox_id = this.humanReadableId();
    this.mapbox_source = safeFmt`source_${this.humanReadableId()}`;

    this.mapboxSrcGroup = new MapboxSourceGroup(this.mapbox_id, [
      "line-data-source",
      "point-data-source",
      "polygon-data-source",
    ]);

    addSource(this.scene.getMapboxMap(), [
      {
        sourceId: this.mapboxSrcGroup.id("line-data-source"),
        source: emptyGeoJsonSource(),
      },
      {
        sourceId: this.mapboxSrcGroup.id("point-data-source"),
        source: emptyGeoJsonSource(),
      },
      {
        sourceId: this.mapboxSrcGroup.id("polygon-data-source"),
        source: emptyGeoJsonSource(),
      },
    ]);

    this.mapboxLayerGroup = customGeoJSONMapboxLayerGroup(this.mapbox_id, props, [
      this.mapboxSrcGroup.id("point-data-source"),
      this.mapboxSrcGroup.id("line-data-source"),
      this.mapboxSrcGroup.id("polygon-data-source"),
    ]);
    addLayerGroup(this.scene.getMapboxMap(), this.mapboxLayerGroup.initialSpecs());
    this.setEventListeners();
  }

  setEventListeners() {
    const mapBoxMap = this.scene.getMapboxMap();
    // Change the cursor to a pointer when over POI
    mapBoxMap.on("mouseenter", this.mapboxLayerGroup.allIds(), (e) => {
      if (!e.features) {
        return;
      }
      const feature = e.features[0];

      if (feature.geometry.type === "Point") {
        mapBoxMap.getCanvas().style.cursor = "pointer";
      }
    });

    // Revert the cursor back when not hovering
    mapBoxMap.on("mouseleave", this.mapboxLayerGroup.allIds(), (e) => {
      mapBoxMap.getCanvas().style.cursor = "";
    });
    // Add a popup window on click.
    mapBoxMap.on("click", this.mapboxLayerGroup.allIds(), (e) => {
      if (!e.features) {
        return;
      }
      const feature = e.features[0];
      // Ensure that if the map is zoomed out such that multiple
      // copies of the feature are visible, the popup appears
      // over the copy being pointed to.
      if (feature.geometry.type === "Point") {
        const coordinates = feature.geometry.coordinates;
        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
          coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }
        this.popup
          .setLngLat(new mapboxgl.LngLat(coordinates[0], coordinates[1]))
          .setHTML(createGeojsonPOIPopover(e.features))
          .addTo(mapBoxMap);
        return;
      }
    });
  }

  beforeRender(): void {
    // If display datetime has changed, redraw is necessary
    if (
      this.displayDateTime.changed(
        roundToNearestCacheableTime(this.scene.getDisplayTimeWithOffset(), this.props.kind as LayerKindName).toISO(),
      )
    ) {
      this.popup.remove();
      this.dirty = true;
    }

    // If layer is dirty, trigger redraw and reset flag to indicate, that it's clean
    if (this.dirty) {
      if (!this.props.user_file_id) {
        this.clearGeoJsonSource();
        this.dirty = false;
        return;
      }

      //Todo: check what if null or not there anymore?
      const requestOption = this.createCustomGeoJsonRequestOption();
      if (!requestOption) {
        this.clearGeoJsonSource();
        this.dirty = false;
        return;
      }
      this.updateGeoJsonSource(
        requestOption,
        roundToNearestCacheableTime(this.scene.getDisplayTimeWithOffset(), this.props.kind as LayerKindName),
      );

      const thresholdFlagChanged = this.thresholdFlag.changed(
        !isEmpty(this.props.thresholds) || !isEmpty(this.props.advanced_thresholds),
      );

      if (thresholdFlagChanged) {
        this.updatePaintPropertiesForCurrentSource();
        this.updateVisibility(this.props.show);
      }

      this.dirty = false;
    }

    const nowPending = this.isRebuffering(this.scene.getDisplayTimeWithOffset());
    if (this.isPending.changed(nowPending)) {
      if (this.props?.id != null) {
        reportLayerLoading(this.props.id, this.isPending.value ?? false);
      }
    }
  }

  checker(): PropsChecker<CustomGeoJSONLayerOutput, LayerBase<CustomGeoJSONLayerOutput>> {
    type CheckerProps = CheckedProps<CustomGeoJSONLayerOutput>;
    const checkLayerProp = (property: CheckerProps) => (prev: any, curr: any) => {
      const changed = prev[property] !== curr[property];
      if (changed) {
        this.setLayerProps({ [property]: curr[property] });
        switch (property) {
          case "show":
            this.updateVisibility(curr.show);
            break;
          case "legend_visible":
            break;
          case "opacity":
            this.updatePaintPropertiesForCurrentSource(curr.opacity);
            break;
          case "custom_options":
            {
              if (
                curr.custom_options.segmentLength !== prev.custom_options.segmentLength ||
                curr.custom_options.units !== prev.custom_options.units ||
                curr.custom_options.aggregate_value !== prev.custom_options.aggregate_value ||
                curr.custom_options.show_values !== prev.custom_options.show_values
              ) {
                this.dirty = true;
              }

              if (
                curr.custom_options.show_values !== prev.custom_options.show_values ||
                curr.custom_options.value_color !== prev.custom_options.value_color ||
                curr.custom_options.value_text_size !== prev.custom_options.value_text_size
              ) {
                this.updatePaintPropertiesForCurrentSource();
                this.updateVisibility(curr.show);
              } else {
                this.updatePaintPropertiesForCurrentSource();
              }
            }
            break;
          case "file_id":
          case "user_file_id":
            this.dirty = true;
            break;
          case "calibrated":
            break;
          case "advanced_thresholds":
          case "thresholds":
            {
              this.updatePaintPropertiesForCurrentSource();
              this.updateVisibility(curr.show);
              if (curr[property] !== prev[property]) {
                this.dirty = true;
              }
            }
            break;
          case "model":
          case "ens_select":
          case "parameter_unit":
            this.dirty = true;
            break;
          default: {
            const _exhaustive: never = property;
            return _exhaustive;
          }
        }
      }
      return changed;
    };
    return {
      calibrated: checkLayerProp("calibrated"),
      file_id: checkLayerProp("file_id"),
      user_file_id: checkLayerProp("user_file_id"),
      opacity: checkLayerProp("opacity"),
      show: checkLayerProp("show"),
      custom_options: checkLayerProp("custom_options"),
      parameter_unit: checkLayerProp("parameter_unit"),
      ens_select: checkLayerProp("ens_select"),
      thresholds: checkLayerProp("thresholds"),
      advanced_thresholds: checkLayerProp("advanced_thresholds"),
      legend_visible: checkLayerProp("legend_visible"),
      model: checkLayerProp("model"),
    };
  }
  private updateGeoJsonSource(customGeoJsonRequestOption: CustomGeoJsonRequestOption, datetime?: DateTime) {
    const animationPrefetchContext = this.resolveAnimationPrefetchContext();
    const { asynchronous } = networkCaches.custom_geojson_cache.retrieveCustomGeoJSON(
      customGeoJsonRequestOption,
      datetime,
      animationPrefetchContext,
    );

    asynchronous
      .then((slicedGeoJSON) => {
        updateSourceData(
          this.scene.getMapboxMap(),
          this.mapboxSrcGroup.id("line-data-source"),
          slicedGeoJSON.lineStrings,
        );
        updateSourceData(this.scene.getMapboxMap(), this.mapboxSrcGroup.id("point-data-source"), slicedGeoJSON.points);
        updateSourceData(
          this.scene.getMapboxMap(),
          this.mapboxSrcGroup.id("polygon-data-source"),
          slicedGeoJSON.polygons,
        );
      })
      .catch((error) => {
        logger.error("Error updating GeoJSON source:", error);
      });
  }

  createRequest(): BaseRequest {
    return { url: this.props.file_id };
  }
  getActiveWeatherParametersAsString(): { model: string; parameter: string }[] {
    return [];
  }

  fetchData(timeFrame: DateTime, animationPrefetchContext?: AnimationPrefetchContext): VoidAsyncResult {
    // Prefetch for animation/time-scene. This must "arm" the cache entry for the given time
    // so `isRebuffering(...)` can observe `StillPending`.
    if (!this.props.user_file_id) {
      return createResolvedAsyncResult(undefined);
    }

    const dateTimeWithOffset = roundToNearestCacheableTime(
      this.scene.getDateTimeWithOffset(timeFrame),
      this.props.kind as LayerKindName,
    );

    const requestOption = this.createCustomGeoJsonRequestOption();
    if (!requestOption) {
      return createResolvedAsyncResult(undefined);
    }
    const cacheableAnimationPrefetchContext = this.resolveAnimationPrefetchContext(animationPrefetchContext);

    // Start (or join) the request for this timestamp. No mapbox source updates here.
    const result = networkCaches.custom_geojson_cache.retrieveCustomGeoJSON(
      requestOption,
      dateTimeWithOffset,
      cacheableAnimationPrefetchContext,
    );
    return asVoidAsyncResult(result);
  }

  private createCustomGeoJsonRequestOption(): CustomGeoJsonRequestOption | null {
    if (this.props.user_file_id == null) {
      return null;
    }

    return {
      user_file_id: this.props.user_file_id,
      model: this.props.model,
      parameter_unit: this.props.parameter_unit,
      customOption: this.props.custom_options,
      thresholds: transformColorThresholds(this.props.thresholds),
      advancedThresholds: transformAdvancedThresholds(this.props?.advanced_thresholds),
    };
  }

  private createCacheableAnimationPrefetchContext(
    animationPrefetchContext: AnimationPrefetchContext,
  ): CustomGeoJSONAnimationFrameContext | null {
    const frameTimestampsByMillis = new Map<number, DateTime>();
    for (const frameTimestamp of animationPrefetchContext.frameTimestamps) {
      const cacheableTimestamp = roundToNearestCacheableTime(
        this.scene.getDateTimeWithOffset(frameTimestamp),
        this.props.kind as LayerKindName,
      );
      frameTimestampsByMillis.set(cacheableTimestamp.toMillis(), cacheableTimestamp);
    }

    const frameTimestamps = Array.from(frameTimestampsByMillis.values()).sort(
      (left, right) => left.toMillis() - right.toMillis(),
    );
    const windowStart = frameTimestamps[0];
    const windowEnd = frameTimestamps[frameTimestamps.length - 1];
    if (!windowStart || !windowEnd) {
      return null;
    }

    return {
      windowStart,
      windowEnd,
      temporalResolution: animationPrefetchContext.temporalResolution,
      frameTimestamps,
      windowKey: [
        windowStart.toUTC().toISO(),
        windowEnd.toUTC().toISO(),
        animationPrefetchContext.temporalResolution.toISO(),
        frameTimestamps.length,
      ].join("|"),
    };
  }

  private resolveAnimationPrefetchContext(
    animationPrefetchContext?: AnimationPrefetchContext,
  ): CustomGeoJSONAnimationFrameContext | null {
    const activeAnimationPrefetchContext =
      animationPrefetchContext ??
      (this.scene.isAnimation() ? getCurrentAnimationPrefetchContext() ?? this.animationPrefetchContext : null);
    if (!activeAnimationPrefetchContext) {
      return null;
    }

    const cacheableAnimationPrefetchContext =
      this.createCacheableAnimationPrefetchContext(activeAnimationPrefetchContext);
    this.animationPrefetchContext = cacheableAnimationPrefetchContext;
    return cacheableAnimationPrefetchContext;
  }

  private humanReadableId() {
    return safeFmt`metx.customgeojsonlayer#${this.uid}`;
  }

  get mapboxIndex(): string {
    return this.mapboxLayerGroup.mainId();
  }

  isRebuffering(time: DateTime): boolean {
    if (!this.props.user_file_id) {
      return false;
    }

    const backendHydrateMode = isGeojsonBackendHydrationEnabled();
    const dateTimeWithOffset = roundToNearestCacheableTime(
      this.scene.getDateTimeWithOffset(time),
      this.props.kind as LayerKindName,
    );
    const requestOption = this.createCustomGeoJsonRequestOption();
    if (!requestOption) {
      return false;
    }

    const cacheId = constructHashId(
      this.props.user_file_id,
      dateTimeWithOffset,
      this.props.model,
      this.props.parameter_unit,
      this.props.custom_options,
      transformAdvancedThresholds(this.props?.advanced_thresholds),
      !isEmpty(this.props.thresholds),
      backendHydrateMode,
    );

    const { synchronous } = networkCaches.custom_geojson_cache.peekCache(cacheId);
    if (synchronous === SynchrounousState.StillPending) {
      return true;
    }

    // Wait until the underlying sources report loaded to avoid exporting partially rendered frames.
    const map = this.scene.getMapboxMap();

    const sourceIds = [
      this.mapboxSrcGroup.id("line-data-source"),
      this.mapboxSrcGroup.id("point-data-source"),
      this.mapboxSrcGroup.id("polygon-data-source"),
    ];

    let sawExpectedSource = false;
    for (const sourceId of sourceIds) {
      if (!map.getSource(sourceId)) {
        // Source may be missing during teardown; don't block.
        continue;
      }
      sawExpectedSource = true;
      if (!map.isSourceLoaded(sourceId)) {
        return true;
      }
    }

    if (!sawExpectedSource && map.isStyleLoaded?.() === false) {
      return true;
    }

    return false;
  }

  removeLayer(): void {
    removeLayerGroup(this.scene.getMapboxMap(), this.mapboxLayerGroup.allIds());
    removeSource(this.scene.getMapboxMap(), this.mapboxSrcGroup.allIds());
    if (this.props?.id != null) {
      clearLayerLoading(this.props.id);
    }
  }

  moveZIndex(beforeLayerId?: string | undefined): void {
    moveZIndex(this.scene.getMapboxMap(), this.mapboxLayerGroup.allIds(), beforeLayerId);
  }

  private clearGeoJsonSource(): void {
    const emptyGeoJSON: GeoJSON.FeatureCollection = {
      type: "FeatureCollection",
      features: [],
    };

    updateSourceData(this.scene.getMapboxMap(), this.mapboxSrcGroup.id("line-data-source"), emptyGeoJSON);
    updateSourceData(this.scene.getMapboxMap(), this.mapboxSrcGroup.id("point-data-source"), emptyGeoJSON);
    updateSourceData(this.scene.getMapboxMap(), this.mapboxSrcGroup.id("polygon-data-source"), emptyGeoJSON);
  }

  private updateVisibility(show: boolean): void {
    const map = this.scene.getMapboxMap();
    const hasThresholds = !isEmpty(this.props.thresholds) || !isEmpty(this.props.advanced_thresholds);
    const showValues = !!this.props.custom_options.show_values && show;

    updateMapboxLayoutProperties(map, this.mapboxLayerGroup.id("polygon-layer"), {
      visibility: hasThresholds && show ? "visible" : "none",
    });

    updateMapboxLayoutProperties(map, this.mapboxLayerGroup.id("polygon-layer-outline"), {
      visibility: !hasThresholds && show ? "visible" : "none",
    });

    updateMapboxLayoutProperties(map, this.mapboxLayerGroup.id("line-layer", "point-layer"), {
      visibility: show ? "visible" : "none",
    });

    updateMapboxLayoutProperties(map, this.mapboxLayerGroup.id("line-value-layer", "polygon-value-layer"), {
      visibility: showValues ? "visible" : "none",
    });
  }

  private updatePaintProperties(
    colorExpression: Expression | string,
    opacity?: number,
    polygonColorExpression: Expression | string = colorExpression,
  ): void {
    const map = this.scene.getMapboxMap();
    const lineWidth = this.props.custom_options.line_width || customGeoJsonLayerConfig.defaultLineWidth;
    const valueTextSize = this.props.custom_options.value_text_size || customGeoJsonLayerConfig.defaultValueTextSize;
    const valueColor =
      this.props.custom_options.value_color ||
      this.props.custom_options.line_color ||
      customGeoJsonLayerConfig.defaultValueColor;

    updateMapboxPaintProperties(map, this.mapboxLayerGroup.id("line-layer"), {
      "line-color": colorExpression,
      "line-width": lineWidth,
      ...(opacity != null && { "line-opacity": opacity }),
    });

    updateMapboxPaintProperties(map, this.mapboxLayerGroup.id("polygon-layer-outline"), {
      "line-color": colorExpression,
      "line-width": lineWidth,
      ...(opacity != null && { "line-opacity": opacity }),
    });

    updateMapboxPaintProperties(map, this.mapboxLayerGroup.id("polygon-layer"), {
      "fill-color": [
        "case",
        ["all", ["has", "threshold_color"], ["has", "threshold_value"]],
        "transparent",
        polygonColorExpression,
      ],
      ...(opacity != null && { "fill-opacity": opacity }),
    });

    updateMapboxPaintProperties(map, this.mapboxLayerGroup.id("point-layer"), {
      ...(opacity != null && { "circle-opacity": opacity }),
      "circle-color": [
        "case",
        // 1) EON / threshold-based points
        ["has", "threshold_color"],
        thresholdColorExpressionFromProperties(this.getLineColor()),

        // 2) Cadent flood alert points
        ["has", "ta_code"],
        ["case", ["has", "color"], ["get", "color"], "gray"],

        // 3) Generic value-based coloring
        colorExpression,
      ],
    });

    updateMapboxLayoutProperties(map, this.mapboxLayerGroup.id("line-value-layer", "polygon-value-layer"), {
      "text-size": valueTextSize,
    });

    updateMapboxPaintProperties(map, this.mapboxLayerGroup.id("line-value-layer", "polygon-value-layer"), {
      "text-color": valueColor,
      "text-halo-color": "#ffffff",
      "text-halo-width": 1,
    });
  }

  private updatePaintPropertiesForCurrentSource(opacity?: number): void {
    const colorExpression = this.computeColorExpression();
    this.updatePaintProperties(colorExpression, opacity);
  }

  private getLineColor(): string {
    return this.props.custom_options.line_color || "black";
  }

  private computeColorExpression(): Expression | string {
    const defaultColor = this.getLineColor();
    const advancedThresholds = transformAdvancedThresholds(this.props.advanced_thresholds);
    const thresholdColors = transformColorThresholds(this.props.thresholds);

    return advancedThresholds.length
      ? advancedThresholdColorExpression(defaultColor, advancedThresholds)
      : thresholdColorExpression(defaultColor, thresholdColors);
  }
}
